/** @jsxImportSource @emotion/react */
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { useParams, Redirect } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import Navbar from "../Navbar";
import Step1 from "pages/drugOrigin/Step1";
import Step2 from "pages/drugOrigin/Step2";
import Step3 from "pages/drugOrigin/Step3";
import Step4 from "pages/drugOrigin/Step4";
import FinalStep from "./FinalStep";
import { DrugOrigin, setFormData } from "utils/drugOrigin";
import {
  useAdminGetOrigin,
  useAdminUpdateOrigin,
} from "utils/hooks/admin.hook";
import CircularProgress from "@material-ui/core/CircularProgress";
import UnsavedChangeDialog from "component/UnsavedChangeDialog";

function AdminDrugOrigin() {
  const theme = useTheme();
  const [redirectionPath, setRedirectionPath] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [valuesChanged, setValuesChanged] = useState(false);
  const params = useParams<{ id: string }>();
  const { mutate: updateOrigin } = useAdminUpdateOrigin(params.id);
  const { isLoading, isError, origin } = useAdminGetOrigin(params.id);
  const methods = useForm<DrugOrigin>({
    mode: "onChange",
    defaultValues: {
      productionCountriesDrugFormulation: [
        {
          countryId: null,
          percentage: null,
        },
      ],
      productionCountriesPrimaryPackaging: [
        {
          countryId: null,
          percentage: null,
        },
      ],
    },
  });
  const { watch, setValue } = methods;

  const handleStep = (type: "decrement" | "increment") => {
    setStep((step) => (type === "decrement" ? step - 1 : step + 1));
  };

  useEffect(() => {
    if (origin) setFormData(origin, setValue);
  }, [origin, setValue]);

  const handleSave = () => {
    updateOrigin(watch());
  };

  if (isLoading) {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return <Redirect to="/admin" />;
  }

  return (
    <>
      <UnsavedChangeDialog
        redirectionPath={redirectionPath}
        setRedirectionPath={setRedirectionPath}
        valuesChanged={valuesChanged}
        setValuesChanged={setValuesChanged}
        saveAction={handleSave}
        watch={watch}
      />
      <div
        css={{
          flexGrow: 1,
          backgroundColor: theme.white,
          color: theme.black,
          display: "flex",
        }}
      >
        <Navbar customRedirection={setRedirectionPath} />
        <FormProvider {...methods}>
          <form
            css={{
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            {step <= 1 && (
              <Step1
                handleSaveBeforeLeave={setRedirectionPath}
                handleStep={handleStep}
              />
            )}
            {step === 2 && <Step2 handleStep={handleStep} />}
            {step === 3 && <Step3 handleStep={handleStep} />}
            {step === 4 && (
              <Step4
                setValuesChanged={setValuesChanged}
                plants={[]}
                handleStep={handleStep}
              />
            )}
            {step === 5 && <FinalStep handleStep={handleStep} />}
          </form>
        </FormProvider>
      </div>
    </>
  );
}

export default AdminDrugOrigin;
